/**
 * iOS/Android Native Push Notification Servisi (Capacitor)
 * 
 * Bu servis SADECE native platformlarda (Capacitor iOS/Android) çalışır.
 * Web platformunda çağrılMAZ — notificationService.ts Web Push'ı yönetir.
 * 
 * Akış:
 * 1. İzin iste (iOS'ta sistem dialog'u gösterir)
 * 2. APNs/FCM'e kayıt ol → native token al
 * 3. Token'ı Firestore'a kaydet (mevcut fcmTokens dizisine ekle)
 * 4. Gelen bildirimleri dinle
 * 
 * Gereksinimler:
 * - iOS: APNs Authentication Key (.p8) Firebase Console'a yüklenmiş olmalı
 * - iOS: GoogleService-Info.plist ios/App/App/ dizininde olmalı
 * - iOS: Push Notifications + Background Modes capability etkin olmalı
 * - Android: MyFirebaseMessagingService.java zaten yerel push'ı yönetiyor,
 *   bu servis sadece Capacitor native ortamda ek olarak çalışır.
 * 
 * @see .agent/docs/fcm_push_notification_mimarisi.md
 */
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { auth, db, isFirebaseActive } from './firebase';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { claimPushToken } from './notificationService';

/**
 * Native push notification sistemini başlatır.
 * App.tsx'ten sadece isNativePlatform() true ise çağrılır.
 */
export async function initNativePushNotifications(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
        console.log('[NativePush] Web platformu tespit edildi, atlanıyor.');
        return;
    }

    try {
        // 1. İzin kontrolü
        let permStatus = await PushNotifications.checkPermissions();

        if (permStatus.receive === 'prompt') {
            permStatus = await PushNotifications.requestPermissions();
        }

        if (permStatus.receive !== 'granted') {
            console.warn('[NativePush] Bildirim izni verilmedi.');
            return;
        }

        // 2. Dinleyiciler register()'dan ÖNCE eklenmeli, yoksa 'registration' olayı kaçabilir
        PushNotifications.addListener('registration', async (token) => {
            console.log('[NativePush] Token alındı:', token.value.substring(0, 20) + '...');
            await saveNativeTokenToFirestore(token.value);
        });

        // Kayıt hatası
        PushNotifications.addListener('registrationError', (error) => {
            console.error('[NativePush] Kayıt hatası:', JSON.stringify(error));
        });

        // 3. APNs'e kayıt ol (iOS'ta dönen token ham APNs device token'ıdır, FCM token'ı DEĞİL)
        await PushNotifications.register();

        // 4. Uygulama ÖN PLANDA iken gelen bildirim
        PushNotifications.addListener('pushNotificationReceived', (notification) => {
            console.log('[NativePush] Ön plan bildirimi:', notification.title, notification.body);
            // iOS'ta ön planda bildirim otomatik gösterilir (presentationOptions: ['alert'] sayesinde)
        });

        // 5. Kullanıcı bildirime TIKLADIĞINDA
        PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
            console.log('[NativePush] Bildirim tıklandı:', action.notification.title);
            const data = action.notification.data;
            if (data?.url || data?.click_url) {
                // Deep link navigation
                const targetUrl = data.url || data.click_url;
                if (targetUrl && targetUrl !== '/') {
                    window.location.href = targetUrl;
                }
            }
        });

        console.log('[NativePush] Native push notification sistemi başlatıldı.');
    } catch (error) {
        console.error('[NativePush] Başlatma hatası:', error);
    }
}

/**
 * Native push token'ı Firestore'daki kullanıcı belgesine kaydeder.
 * Mevcut Web VAPID token sistemiyle uyumlu — aynı fcmTokens dizisine eklenir.
 * Backend'deki sendPushToSubscriptions() bu token'ı otomatik olarak kullanır.
 */
async function saveNativeTokenToFirestore(token: string): Promise<void> {
    // Auth henüz hazır değilse localStorage'a kaydet, sonra yüklenecek
    if (!isFirebaseActive || !auth.currentUser) {
        localStorage.setItem('kpss_native_push_token', token);
        // Ayrıca mevcut FCM token key'ine de kaydet (App.tsx uyumu için)
        localStorage.setItem('kpss_fcm_token', token);
        console.log('[NativePush] Token localStorage\'a kaydedildi (auth henüz hazır değil).');
        return;
    }

    try {
        const uid = auth.currentUser.uid;
        const userRef = doc(db, 'users', uid);
        const platform = Capacitor.getPlatform(); // 'ios' veya 'android'

        // Mevcut token listesini çek
        const userSnap = await getDoc(userRef);
        let currentTokens: string[] = [];
        let currentSubs: any[] = [];
        if (userSnap.exists()) {
            currentTokens = userSnap.data().fcmTokens || [];
            currentSubs = userSnap.data().pushSubscriptions || [];
        }

        // Aynı token zaten varsa ekleme, en başa en güncel haliyle ekle (Max 5)
        const filteredTokens = currentTokens.filter((t: string) => t !== token);
        const updatedTokens = [token, ...filteredTokens].slice(0, 5);
        // Abonelikler de aynı token için tekilleştirilip 5 ile sınırlanır (eskiden her açılışta arrayUnion ile büyüyordu).
        // Sunucu en yeniyi updatedAt'e göre seçer; yeni kayıt başa eklenir.
        const updatedSubscriptions = [{
            endpoint: token,
            platform: platform,
            isNative: true,
            isTwa: false,
            updatedAt: new Date().toISOString()
        }, ...currentSubs.filter((s: any) => s?.endpoint !== token)].slice(0, 5);

        await updateDoc(userRef, {
            fcmTokens: updatedTokens,
            pushSubscriptions: updatedSubscriptions
        });

        // localStorage'a da kaydet (App.tsx cached token uyumu)
        localStorage.setItem('kpss_native_push_token', token);
        localStorage.setItem('kpss_fcm_token', token);
        // Aynı cihazda önceden açılmış başka hesapta kalan token silinir (yoksa iki bildirim gelir)
        claimPushToken(token);
        console.log(`[NativePush] ${platform} token Firestore'a kaydedildi.`);
    } catch (error) {
        console.error('[NativePush] Token kayıt hatası:', error);
        localStorage.setItem('kpss_native_push_token', token);
        localStorage.setItem('kpss_fcm_token', token);
    }
}
